import { useAuth } from '@client/hooks/useAuth';
import { useStoreMutation } from '@client/queries/useStoreMutation';
import { useStoreQuery } from '@client/queries/useStoreQuery';
import {
  createTenant,
  deactivateTenant,
  deleteTenant,
  getAllTenants,
  getTenant,
  getUserTenants,
  updateTenant,
  getTenantsBySubscription,
  getTierLimits,
  getApiKeys,
  activateApiKey,
  deactivateApiKey,
  deleteApiKey,
  generateApiKey,
} from '@client/services/tenant.service';
import { EErrorType, type ITenant, type ETier } from '@common/types';

import type { TPaginateProps } from '@client/types/antd.type';
import type { TablePaginationConfig } from 'antd/es/table/interface';

// Query keys
export const tenantKeys = {
  all: ['tenants'] as const,
  lists: () => [...tenantKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) =>
    [...tenantKeys.lists(), filters] as const,
  details: () => [...tenantKeys.all, 'detail'] as const,
  detail: (id: string) => [...tenantKeys.details(), id] as const,
  apiKeys: (tenantId: string, user: any) =>
    [
      ...tenantKeys.detail(tenantId),
      'api-keys',
      user?.uid,
      user?.email,
    ] as const,
  limits: () => [...tenantKeys.all, 'limits'] as const,
  limit: (tier: string) => [...tenantKeys.limits(), tier] as const,
  bySubscription: (subscriptionId: string) =>
    [...tenantKeys.all, 'by-subscription', subscriptionId] as const,
};

export const useTenantQueries = () => {
  const { user } = useAuth();

  // Get all tenants
  const useAllTenants = (params?: TPaginateProps & TablePaginationConfig) =>
    useStoreQuery({
      queryKey: tenantKeys.list({ type: 'all', ...params }),
      queryFn: () => getAllTenants(params),
      select: (result) => result || [],
    });

  // Get user tenants
  const useUserTenants = (params?: TPaginateProps & TablePaginationConfig) =>
    useStoreQuery({
      queryKey: tenantKeys.list({ type: 'user', user, ...params }),
      queryFn: () => getUserTenants(params),
      enabled: !!user,
    });

  // Get tenant by id
  const useUserTenant = (id: string) =>
    useStoreQuery({
      queryKey: tenantKeys.detail(id),
      queryFn: () => getTenant(id),
      enabled: !!id,
    });

  // Create a tenant mutation
  const useCreateTenant = () =>
    useStoreMutation({
      mutationFn: (data: ITenant) => createTenant(data),
      invalidateQueries: [tenantKeys.lists()],
    });

  // Generate API key mutation
  const useGenerateApiKey = () =>
    useStoreMutation({
      mutationFn: ({
        tenant,
        expiresAt,
      }: {
        tenant: ITenant;
        expiresAt: Date | null;
      }) => {
        if (!user) {
          throw new Error(EErrorType.NOT_AUTHENTICATED);
        }
        return generateApiKey(user, tenant, expiresAt);
      },
      invalidateQueries: [tenantKeys.details()],
    });

  // Update tenant mutation
  const useUpdateTenant = () =>
    useStoreMutation({
      mutationFn: ({ id, data }: { id: string; data: ITenant }) =>
        updateTenant(id, data),
      invalidateQueries: [tenantKeys.lists()],
    });

  // Delete tenant mutation
  const useDeleteTenant = () =>
    useStoreMutation({
      mutationFn: (id: string) => deleteTenant(id),
      invalidateQueries: [tenantKeys.lists()],
    });

  // Deactivate tenant mutation
  const useDeactivateTenant = () =>
    useStoreMutation({
      mutationFn: (id: string) => deactivateTenant(id),
      invalidateQueries: [tenantKeys.lists()],
    });

  // Get tenants by subscription
  const useTenantsBySubscription = (subscriptionId: string) =>
    useStoreQuery({
      queryKey: tenantKeys.bySubscription(subscriptionId),
      queryFn: () => getTenantsBySubscription(subscriptionId),
      enabled: !!subscriptionId,
    });

  // Get tier limits mutation
  const useTierLimits = () =>
    useStoreMutation({
      mutationFn: ({ tenant, tier }: { tenant: ITenant; tier: ETier }) =>
        getTierLimits(tenant, tier),
    });

  // Get API keys
  const useApiKeys = (tenantId: string) =>
    useStoreQuery({
      queryKey: tenantKeys.apiKeys(tenantId, user),
      queryFn: () => {
        if (!user) {
          throw new Error(EErrorType.NOT_AUTHENTICATED);
        }
        return getApiKeys(user, tenantId);
      },
      enabled: !!user && !!tenantId,
    });

  // Activate API key mutation
  const useActivateApiKey = () =>
    useStoreMutation({
      mutationFn: ({
        tenantId,
        apiKeyId,
      }: {
        tenantId: string;
        apiKeyId: string;
      }) => {
        if (!user) {
          throw new Error(EErrorType.NOT_AUTHENTICATED);
        }
        return activateApiKey(user, tenantId, apiKeyId);
      },
      invalidateQueries: [tenantKeys.details()],
    });

  // Deactivate API key mutation
  const useDeactivateApiKey = () =>
    useStoreMutation({
      mutationFn: ({
        tenantId,
        apiKeyId,
      }: {
        tenantId: string;
        apiKeyId: string;
      }) => {
        if (!user) {
          throw new Error(EErrorType.NOT_AUTHENTICATED);
        }
        return deactivateApiKey(user, tenantId, apiKeyId);
      },
      invalidateQueries: [tenantKeys.details()],
    });

  // Delete API key mutation
  const useDeleteApiKey = () =>
    useStoreMutation({
      mutationFn: ({
        tenantId,
        apiKeyId,
      }: {
        tenantId: string;
        apiKeyId: string;
      }) => deleteApiKey(user, tenantId, apiKeyId),
      invalidateQueries: [tenantKeys.details()],
    });

  return {
    useAllTenants,
    useUserTenants,
    useUserTenant,
    useCreateTenant,
    useUpdateTenant,
    useDeleteTenant,
    useDeactivateTenant,
    useTenantsBySubscription,
    useTierLimits,
    useApiKeys,
    useActivateApiKey,
    useDeactivateApiKey,
    useDeleteApiKey,
    useGenerateApiKey,
  };
};
